import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { createPortal } from 'react-dom';

const CreateCustomFieldModal = ({ isOpen, onClose, supabase, onSuccess }) => {
  const [fieldName, setFieldName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleSave = async () => {
    if (!fieldName.trim()) {
      toast.error('Field name cannot be empty');
      return;
    }

    setIsSaving(true);
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) throw new Error('User not authenticated');

      const newField = {
        account_id: accountId,
        field_name: fieldName.trim(),
      };

      const { data, error } = await supabase
        .from('brain_post_call_custom_fields')
        .insert(newField)
        .select()
        .single();

      if (error) {
        if (error.code === '23505') { // unique_violation
          toast.error('This field name already exists.');
        } else {
          throw error;
        }
      } else {
        toast.success('Custom field created successfully');
        onSuccess(data);
        onClose();
      }
    } catch (err) {
      console.error('Error saving custom field:', err);
      toast.error('Failed to save custom field');
    } finally {
      setIsSaving(false);
      setFieldName('');
    }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.3 } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
  };

  const popupVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: { 
      scale: 1, 
      opacity: 1, 
      y: 0,
      transition: { type: "spring", damping: 30, stiffness: 300, duration: 0.4 }
    },
    exit: { scale: 0.98, opacity: 0, y: 10, transition: { duration: 0.2 } }
  };

  if (!isOpen) return null;

  const popupContent = (
    <motion.div
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={backdropVariants}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4"
      onClick={onClose}
    >
      <motion.div
        ref={popupRef}
        variants={popupVariants}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100/80 rounded-lg">
              <Sparkles size={16} className="text-gray-700" />
            </div>
            <div>
              <h2 className="font-medium text-lg text-gray-900">Create Custom Field</h2>
              <p className="text-xs text-gray-500 mt-0.5">Add a new field for your conditional triggers.</p>
            </div>
          </div>
          <motion.button
            whileHover={{ scale: 1.05, backgroundColor: '#f3f4f6' }}
            whileTap={{ scale: 0.95 }}
            onClick={onClose}
            className="rounded-full h-8 w-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </motion.button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="custom-field-name" className="block text-sm font-medium text-gray-700 mb-2">
              Field Name
            </label>
            <input
              ref={inputRef}
              id="custom-field-name"
              type="text"
              value={fieldName}
              onChange={(e) => setFieldName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              placeholder="e.g., 'Appointment Booked'"
              className="w-full border border-gray-200 rounded-xl h-11 px-4 text-sm 
                focus:outline-none focus:ring-2 focus:ring-black/50 focus:border-transparent
                placeholder:text-gray-400"
              disabled={isSaving}
            />
             <p className="text-xs text-gray-500 mt-2">
              This name will appear in the "Field" dropdown. It must be unique.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 flex justify-end gap-3 border-t">
          <motion.button
            whileHover={{ scale: 1.02, backgroundColor: '#f9fafb' }}
            whileTap={{ scale: 0.98 }}
            onClick={onClose}
            className="py-2.5 px-4 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium
              bg-white hover:bg-gray-50 transition-colors"
            disabled={isSaving}
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            disabled={isSaving || !fieldName.trim()}
            className="py-2.5 px-5 rounded-xl bg-black text-white text-sm font-medium
              disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSaving ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full"
                />
                Saving...
              </>
            ) : (
              "Save Field"
            )}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );

  return createPortal(popupContent, document.body);
};

export default CreateCustomFieldModal; 